import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { portalApi } from '@/lib/api';

// Staff self-service portal session — deliberately SEPARATE from the admin/management console
// (StaffAuthContext). Same credentials, but its own token key so a manager can be signed into the
// portal and the admin console at the same time without one clobbering the other.
const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [staff, setStaff] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem('tiffin_portal_token');
    if (!token) {
      setStaff(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await portalApi.get('/auth/staff/me');
      setStaff(data.staff);
    } catch {
      localStorage.removeItem('tiffin_portal_token');
      setStaff(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await portalApi.post('/auth/staff/login', { email, password });
    localStorage.setItem('tiffin_portal_token', data.token);
    setStaff(data.staff);
    return data.staff;
  };

  const logout = () => {
    localStorage.removeItem('tiffin_portal_token');
    setStaff(null);
  };

  const hasRole = (...roles) => staff && roles.includes(staff.role);

  return (
    <PortalAuthContext.Provider value={{ staff, loading, login, logout, hasRole, refresh: loadMe }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export const usePortalAuth = () => useContext(PortalAuthContext);
